import { useState, useEffect } from 'react';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { Form, FormActions } from '@/components/forms/Form';
import { FormInput } from '@/components/forms/FormField';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuth, useAuthStore } from '@/features/auth/hooks/useAuth';

/** Public branding shape returned by GET /settings/branding/public. */
interface PublicBranding {
  firmName: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  fontColor: string | null;
}

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const mfaSchema = z.object({
  code: z
    .string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d+$/, 'Code must contain only digits'),
});

type MfaFormValues = z.infer<typeof mfaSchema>;

/**
 * Login page with email/password form and MFA verification step.
 * Handles the full authentication flow including:
 * - Email/password credential entry
 * - TOTP MFA verification when required
 * - OAuth SSO options
 * - Password reset link
 *
 * Validates: Requirements 2.1, 2.2
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyMfa } = useAuth();
  const { mfaStatus } = useAuthStore();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [branding, setBranding] = useState<PublicBranding | null>(null);

  // Load the firm's public branding (logo + name) for the pre-auth screen.
  useEffect(() => {
    const tenantId = import.meta.env.VITE_DEFAULT_TENANT_ID;
    if (!tenantId) return;
    let cancelled = false;
    apiClient
      .get<ApiResponse<PublicBranding>>('/settings/branding/public', {
        headers: { 'x-tenant-id': tenantId },
      })
      .then((res) => {
        if (!cancelled) setBranding(res.data.data);
      })
      .catch(() => {
        // Branding is optional on the login screen — ignore failures.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Full-screen gradient from the firm's branding primary → secondary colors.
  // Falls back to the static brand palette hexes while branding loads or when
  // the tenant hasn't configured colors.
  const from = branding?.primaryColor || '#2563eb';
  const to = branding?.secondaryColor || branding?.primaryColor || '#1e3a8a';
  const backgroundStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
  };

  // Firm logo + name header shown above the login/MFA card content.
  const BrandHeader = () =>
    branding?.logoUrl || branding?.firmName ? (
      <div className="mb-4 flex flex-col items-center gap-2">
        {branding.logoUrl && (
          <img
            src={branding.logoUrl}
            alt={`${branding.firmName || 'Firm'} logo`}
            className="h-12 w-auto max-w-[200px] object-contain"
          />
        )}
        {branding.firmName && (
          <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {branding.firmName}
          </span>
        )}
      </div>
    ) : null;

  function handleLogin(values: LoginFormValues) {
    setLoginError(null);
    const tenantId = import.meta.env.VITE_DEFAULT_TENANT_ID;
    if (!tenantId) {
      setLoginError('Tenant configuration is missing. Please contact your administrator.');
      return;
    }
    login.mutate(
      { ...values, tenantId },
      {
        onSuccess: (data) => {
          if (!data.mfaRequired) {
            navigate('/');
          }
        },
        onError: (error) => {
          if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as {
              response?: { data?: { error?: { message?: string; code?: string } } };
            };
            const errorData = axiosError.response?.data?.error;
            if (errorData?.code === 'ACCOUNT_LOCKED') {
              setLoginError(
                'Account is locked due to too many failed attempts. Please try again in 30 minutes.',
              );
            } else {
              setLoginError(errorData?.message || 'Invalid email or password. Please try again.');
            }
          } else {
            setLoginError('An unexpected error occurred. Please try again.');
          }
        },
      },
    );
  }

  function handleMfaVerify(values: MfaFormValues) {
    setMfaError(null);
    verifyMfa.mutate(
      { code: values.code },
      {
        onSuccess: () => {
          navigate('/');
        },
        onError: () => {
          setMfaError('Invalid verification code. Please try again.');
        },
      },
    );
  }

  if (mfaStatus === 'required' || mfaStatus === 'verifying') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4" style={backgroundStyle}>
        <Card className="w-full max-w-md" padding="lg">
          <div className="mb-6 text-center">
            <BrandHeader />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Two-Factor Authentication
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          {mfaError && (
            <div
              className="mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-400"
              role="alert"
            >
              {mfaError}
            </div>
          )}

          <Form<MfaFormValues>
            schema={mfaSchema}
            defaultValues={{ code: '' }}
            onSubmit={handleMfaVerify}
          >
            {(form) => (
              <>
                <FormInput
                  name="code"
                  control={form.control}
                  label="Verification Code"
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  required
                />
                <FormActions className="justify-center">
                  <Button type="submit" loading={verifyMfa.isPending} className="w-full">
                    Verify
                  </Button>
                </FormActions>
              </>
            )}
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={backgroundStyle}
      data-testid="login-page"
    >
      <Card className="w-full max-w-md" padding="lg">
        <div className="mb-6 text-center">
          <BrandHeader />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Sign In</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Sign in to your back-office account
          </p>
        </div>

        {loginError && (
          <div
            className="mb-4 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-400"
            role="alert"
            data-testid="login-error"
          >
            {loginError}
          </div>
        )}

        <Form<LoginFormValues>
          schema={loginSchema}
          defaultValues={{ email: '', password: '' }}
          onSubmit={handleLogin}
          data-testid="login-form"
        >
          {(form) => (
            <>
              <FormInput
                name="email"
                control={form.control}
                label="Email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
              <FormInput
                name="password"
                control={form.control}
                label="Password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
              <FormActions className="flex-col items-stretch gap-4">
                <Button type="submit" loading={login.isPending} className="w-full">
                  Sign In
                </Button>
              </FormActions>
            </>
          )}
        </Form>

        <div className="mt-4 text-center">
          <Link
            to="/auth/password-reset"
            className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
          >
            Forgot your password?
          </Link>
        </div>

        {/* OAuth SSO - commented out until providers are configured
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border dark:border-border-dark" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500 dark:bg-surface-dark-secondary dark:text-gray-400">
              Or continue with
            </span>
          </div>
        </div>

        <OAuthButtons />
        */}
      </Card>
    </div>
  );
}
